import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faLock,
  faSignInAlt,
} from "@fortawesome/free-solid-svg-icons";
import "./Login.css";

const LoginPage = ({ setIsAuthenticated, setUserRole }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const credentialRequestPending = useRef(false);

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "username") {
      setUsername(value);
    } else if (name === "password") {
      setPassword(value);
    }
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_API_URL}/login`,
        { username, password }
      );

      setMessage(response.data.message);
      if (response.data.token) {
        const decoded = jwtDecode(response.data.token);
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userrole", decoded.userrole);
        setUserRole(decoded.userrole);
        localStorage.setItem("username", decoded.username);
        localStorage.setItem("email", decoded.email);

        if (decoded.userrole === "driver") {
          localStorage.setItem("driverName", decoded.name);
          localStorage.setItem("driverId", decoded.USER_ID);
          localStorage.setItem("badge", decoded.badge);
        }

        if (rememberMe) {
          localStorage.setItem("rememberedUsername", username);
        } else {
          localStorage.removeItem("rememberedUsername");
        }

        if (window.PasswordCredential && !rememberMe) {
          try {
            const cred = new window.PasswordCredential({
              id: username,
              password: password,
              name: username,
            });
            await navigator.credentials.store(cred);
          } catch (error) {
            console.error("Credential API storage error:", error);
          }
        }

        setTimeout(() => {
          setIsAuthenticated(true);
          navigate("/main", { replace: true });
        }, 50);
      }
    } catch (error) {
      setMessage(
        error.response ? error.response.data.message : "Error logging in."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-fill handler with enhanced cleanup
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const getCredentials = async () => {
      if (credentialRequestPending.current) return;
      credentialRequestPending.current = true;

      try {
        if (navigator.credentials?.get) {
          const credential = await navigator.credentials.get({
            password: true,
            signal: abortController.signal,
          });

          if (isMounted && credential) {
            setUsername(credential.id);
            setPassword(credential.password);
          }
        }
      } catch (error) {
        if (error.name === "InvalidStateError") {
          console.log("Credential request already pending, retrying...");
          setTimeout(() => getCredentials(), 500);
        } else if (error.name !== "AbortError") {
          console.error("Credential retrieval error:", error);
        }
      } finally {
        credentialRequestPending.current = false;
      }
    };

    const timeoutId = setTimeout(() => getCredentials(), 100);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(timeoutId);
      credentialRequestPending.current = false;
    };
  }, []);

  const handleSignup = (e) => {
    e.preventDefault();
    navigate("/signup");
  };

  return (
    <div className="containerlogin">
      {/* Cinematic Fleet Background */}
      <img
        src="TruckImage.png"
        alt="Fleet Truck Background"
        className="login-truck-bg"
      />
      <div className="login-bg-overlay" />

      {/* Main Glassmorphism Form Card */}
      <form
        className="loginform"
        onSubmit={handleSubmit}
        method="POST"
        action="/login"
      >
        {/* Al Madina Logistics Logo Header */}
        <div className="login-logos-header">
          <img
            src="almadinalog.png"
            alt="Al Madina Logistics"
            className="login-main-logo"
          />
        </div>

        {/* Username Field */}
        <div className="form-group-login">
          <label className="labellogin" htmlFor="username">
            Login/User Id
          </label>
          <div className="input-wrapper">
            <span className="input-icon">
              <FontAwesomeIcon icon={faUser} />
            </span>
            <input
              type="text"
              className="input-fieldlogin"
              name="username"
              id="username"
              placeholder="Enter your user ID"
              value={username}
              onChange={handleInputChange}
              required
              autoComplete="username webauthn"
              aria-describedby="usernameHelp"
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="form-group-login">
          <label className="labellogin" htmlFor="password">
            Password
          </label>
          <div className="input-wrapper">
            <span className="input-icon">
              <FontAwesomeIcon icon={faLock} />
            </span>
            <input
              type="password"
              className="input-fieldlogin"
              name="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={handleInputChange}
              required
              autoComplete="current-password webauthn"
            />
          </div>
        </div>

        {/* Remember Me */}
        <div className="login-options-row">
          <label className="remember-me-label" htmlFor="rememberMe">
            <input
              type="checkbox"
              className="form-check-input"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              autoComplete="off"
            />
            <span>Remember Me</span>
          </label>
        </div>

        {/* Error / Feedback Message */}
        {message && (
          <div className="login-alert-message">
            <span>{message}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="login-button"
          disabled={isSubmitting}
        >
          <span>{isSubmitting ? "Logging in..." : "Login"}</span>
        </button>

        {/* Secondary Links */}
        <div className="login-links">
          <a href="/signup" onClick={handleSignup} className="signup-highlight">
            Driver Sign Up
          </a>
          <span className="login-links-divider">|</span>
          <a
            href="/reset"
            onClick={(e) => {
              e.preventDefault();
              navigate("/reset");
            }}
            className="signup-highlight"
          >
            Reset Password
          </a>
        </div>

        {/* Powered by Bayanat */}
        <div className="login-powered-by">
          <span>Powered by</span>
          <img
            src="bayanat.png"
            alt="Bayanat Technology"
            className="powered-by-logo"
          />
        </div>
      </form>
    </div>
  );
};

export default LoginPage;
